/**
 * useNativeApp Hook
 * =================
 * 
 * Hook de React para acceder a funcionalidades nativas de la app móvil.
 */

import { useEffect, useState, useCallback } from 'react';
import capacitorService from '../services/capacitorService';

export const useNativeApp = () => {
  const [appInfo, setAppInfo] = useState(null);
  const [pushToken, setPushToken] = useState(null);

  // Detectar plataforma
  const isNative = capacitorService.isNative();
  const isIOS = capacitorService.isIOS();
  const isAndroid = capacitorService.isAndroid();
  const isWeb = capacitorService.isWeb();

  // Inicializar
  useEffect(() => {
    const init = async () => {
      // Obtener info de la app
      const info = await capacitorService.getAppInfo();
      setAppInfo(info);

      // Inicializar app nativa
      if (isNative) {
        await capacitorService.initializeApp();
        
        // Configurar push notifications
        capacitorService.setupPushNotifications(
          (token) => setPushToken(token),
          (_notification) => {
            // Notification received
          },
          (action) => {
            const data = action.notification?.data;
            // Rutas reales de App.js (antes apuntaban a /convocatorias y /plan-estudio, inexistentes).
            if (data?.type === 'convocatoria') {
              window.location.href = '/convocatoria';
            } else if (data?.type === 'study_reminder') {
              window.location.href = '/study-plan';
            }
          }
        );
      }
    };

    init();
  }, [isNative]);

  // Haptics
  const hapticFeedback = useCallback(async (type = 'medium') => {
    await capacitorService.hapticImpact(type);
  }, []);

  const hapticSuccess = useCallback(async () => {
    await capacitorService.hapticNotification('success');
  }, []);

  const hapticError = useCallback(async () => {
    await capacitorService.hapticNotification('error');
  }, []);

  // Sharing
  const share = useCallback(async (title, text, url) => {
    return capacitorService.shareContent(title, text, url);
  }, []);

  const shareResult = useCallback(async (simulacroName, nota, percentil) => {
    return capacitorService.shareSimulacroResult(simulacroName, nota, percentil);
  }, []);

  // Notifications
  const requestNotificationPermission = useCallback(async () => {
    return capacitorService.requestPushPermission();
  }, []);

  const scheduleReminder = useCallback(async (hora, tema, dias) => {
    await capacitorService.scheduleStudyReminder(hora, tema, dias);
  }, []);

  const cancelReminders = useCallback(async () => {
    await capacitorService.cancelStudyReminders();
  }, []);

  // Browser
  const openUrl = useCallback(async (url) => {
    await capacitorService.openExternalUrl(url);
  }, []);

  // Keyboard
  const hideKeyboard = useCallback(async () => {
    await capacitorService.hideKeyboard();
  }, []);

  return {
    // Estado
    isNative,
    isIOS,
    isAndroid,
    isWeb,
    appInfo,
    pushToken,
    
    // Haptics
    hapticFeedback,
    hapticSuccess,
    hapticError,
    
    // Sharing
    share,
    shareResult,
    
    // Notifications
    requestNotificationPermission,
    scheduleReminder,
    cancelReminders,
    
    // Browser
    openUrl,
    
    // Keyboard
    hideKeyboard,
  };
};

export default useNativeApp;
